/**
 * Excel-backed SheetPort. Operations issued in the same tick (the writer issues
 * all of one apply together) run in a single Excel.run. Also wires the real
 * Office objects into a StaticWriter for the custom functions, and gives them
 * the FormulaPort they re-enter and watch formula cells through.
 */
import { settingsStore } from "./footprints";
import { SheetPort, StaticWriter, WriteCell } from "./writer";

type Op = (context: Excel.RequestContext) => void;

let queue: Op[] = [];
let flushing: Promise<void> | null = null;

// --- Own writes ------------------------------------------------------------------
// Every range this add-in writes is remembered for OWN_WRITE_MS, so a worksheet
// change event it caused is told apart from the user's on hosts whose events
// carry no triggerSource (before ExcelApi 1.14).

/** A rectangle of one sheet, 0-based. */
export interface SheetRect {
  sheet: string;
  row: number;
  col: number;
  rows: number;
  cols: number;
}

const OWN_WRITE_MS = 2000;
type OwnWrite = { rect: SheetRect; until: number };

/**
 * The list lives on globalThis, not in this module: taskpane.js and
 * functions.js are separate bundles, each with its own copy of this module,
 * but they share one runtime (the manifest's shared runtime). The watcher runs
 * in functions.js and must see the marks the task pane makes (its picks, Find
 * RICs).
 */
const OWN_KEY = "__eavestOwnWrites";
function ownWrites(): OwnWrite[] {
  const g = globalThis as unknown as Record<string, OwnWrite[] | undefined>;
  const now = Date.now();
  const live = (g[OWN_KEY] ?? []).filter((w) => w.until >= now);
  g[OWN_KEY] = live;
  return live;
}

export function markOwn(rect: SheetRect): void {
  ownWrites().push({ rect, until: Date.now() + OWN_WRITE_MS });
}

/** True when `rect` lies inside a range this add-in wrote in the last OWN_WRITE_MS. */
export function isOwnWrite(rect: SheetRect): boolean {
  return ownWrites().some(
    ({ rect: w }) =>
      w.sheet.toLowerCase() === rect.sheet.toLowerCase() &&
      rect.row >= w.row && rect.row + rect.rows <= w.row + w.rows &&
      rect.col >= w.col && rect.col + rect.cols <= w.col + w.cols
  );
}

function run(op: Op): Promise<void> {
  queue.push(op);
  if (!flushing) {
    flushing = Promise.resolve().then(() => {
      const ops = queue;
      queue = [];
      flushing = null;
      return Excel.run(async (context) => {
        ops.forEach((o) => o(context));
        await context.sync();
      });
    });
  }
  return flushing;
}

const range = (context: Excel.RequestContext, sheet: string, row: number, col: number, rows: number, cols: number): Excel.Range => {
  markOwn({ sheet, row, col, rows, cols });
  return context.workbook.worksheets.getItem(sheet).getRangeByIndexes(row, col, rows, cols);
};

let typedSupport: boolean | null = null;

/** Range.valuesAsJson (typed writes) needs ExcelApi 1.16 (Microsoft 365). */
function typedValues(): boolean {
  if (typedSupport === null) {
    try {
      typedSupport = Office.context.requirements.isSetSupported("ExcelApi", "1.16");
    } catch {
      typedSupport = false;
    }
  }
  return typedSupport;
}

/** A typed cell value: Excel stores it as is (LSEG's XLL writes typed values too). */
export function cellValueOf(cell: WriteCell): Excel.CellValue {
  if (cell.error) return { type: "Error", basicValue: "#N/A", errorType: "NotAvailable" } as Excel.CellValue;
  if (typeof cell.v === "number") return { type: "Double", basicValue: cell.v };
  if (typeof cell.v === "boolean") return { type: "Boolean", basicValue: cell.v };
  return { type: "String", basicValue: cell.v };
}

// Text Excel's input parser could turn into something else: a number or date
// (any digit: "0700", "1/2", "1e5"), a formula ("=", "+", "-", "@"), an error
// ("#N/A"), a boolean, or text already starting with the text prefix.
const PARSED_RE = /\d|^[=+\-@#']|^\s*(true|false)\s*$/i;

/**
 * The Range.values literal of a cell, for hosts without valuesAsJson: text
 * Excel would parse gets the apostrophe text prefix; an error is its literal,
 * which Excel enters as the error itself.
 */
export function literalOf(cell: WriteCell): string | number | boolean {
  if (cell.error) return cell.v;
  return typeof cell.v === "string" && PARSED_RE.test(cell.v) ? `'${cell.v}` : cell.v;
}

export const excelPort: SheetPort = {
  clear: (sheet, row, col, rows, cols) =>
    run((context) => { range(context, sheet, row, col, rows, cols).clear(Excel.ClearApplyTo.contents); }),

  write: (sheet, row, col, cells) =>
    run((context) => {
      const target = range(context, sheet, row, col, cells.length, cells.length ? cells[0].length : 0);
      if (typedValues()) target.valuesAsJson = cells.map((line) => line.map(cellValueOf));
      else target.values = cells.map((line) => line.map(literalOf));
    }),

  format: (sheet, row, col, rows, cols, format) =>
    run((context) => {
      range(context, sheet, row, col, rows, cols).numberFormat = Array.from({ length: rows }, () => new Array(cols).fill(format));
    }),
};

/** The writer used by the custom functions: Excel port, footprints in document settings. */
export function createWriter(): StaticWriter {
  return new StaticWriter(excelPort, settingsStore(Office.context.document.settings));
}

// --- Formula cells ---------------------------------------------------------------

/** A formula cell, 0-based. */
export interface FormulaCell {
  sheet: string;
  row: number;
  col: number;
}

/**
 * What a re-entry found in one cell. `formula`: the cell's formula ("" when it
 * holds none or its sheet is gone; null when it could not be read).
 * `reentered`: the cell holds a formula and was recalculated (Excel
 * re-invoked its functions). `sheetGone`: no sheet has that name any more
 * (deleted, renamed).
 */
export interface Reentry {
  formula: string | null;
  reentered: boolean;
  sheetGone?: boolean;
}

/**
 * What a refresh command covers, as LSEG's PLRefresh commands (golden
 * refresh_out): the selected cells, the active sheet, or every sheet of the
 * workbook (LSEG's All, every open workbook, is the workbook here: Office.js
 * reaches only this one [A]).
 */
export type FormulaScope = "selection" | "sheet" | "workbook";

/** A formula cell and the formula it holds. */
export interface ScopedFormula extends FormulaCell {
  formula: string;
}

/** What the worksheet events tell the custom functions. */
export interface SheetEvents {
  /** A change this add-in did not make, one rectangle per area. */
  change(rect: SheetRect): void;
  /** A worksheet was deleted. */
  sheetDeleted(): void;
}

/**
 * "Re-entry" of a formula cell: Office.js `range.calculate()` on that cell,
 * which makes Excel re-invoke exactly that cell's functions (measured in the
 * post-deploy sideload: re-invoked 17 ms later; a self `copyFrom`, formulas
 * or all, re-invokes nothing). The custom functions use it for the live
 * updates of non-streaming functions and to find out which of a cell's calls
 * are still in its formula. It writes nothing, so it can never overwrite a
 * user's edit.
 */
export interface FormulaPort {
  /**
   * Recalculate each cell and report the formula it holds, read in the same
   * batch: one Excel.run, one sync after the sheet check (ExcelApi 1.6).
   */
  reenter(cells: FormulaCell[]): Promise<Reentry[]>;
  /**
   * Report worksheet changes this add-in did not make (worksheets.onChanged,
   * ExcelApi 1.9; own changes recognised by the event's triggerSource,
   * ExcelApi 1.14, else by isOwnWrite) and sheet deletions
   * (worksheets.onDeleted, ExcelApi 1.7). Registered once.
   */
  watch(events: SheetEvents): void;
  /** The names of the workbook's sheets. */
  sheetNames(): Promise<string[]>;
  /**
   * The formula cells in `scope`, with their formulas, row by row, sheet by
   * sheet. One Excel.run, three syncs and one `formulas` load per rectangle
   * (a selection's area, or a sheet's used range), however many cells.
   */
  formulasIn(scope: FormulaScope): Promise<ScopedFormula[]>;
}

const MAX_ROWS = 1048576;
const MAX_COLS = 16384;

/**
 * The sheet rectangle of an A1 range address: "B2", "B2:D5", "3:5" (whole
 * rows), "C:E" (whole columns), `$` allowed; a sheet prefix is ignored. Null
 * when unparsable (a multi-area address: see rectsOfAddress).
 */
export function rectOfAddress(sheet: string, address: string): SheetRect | null {
  const local = address.slice(address.lastIndexOf("!") + 1).replace(/\$/g, "");
  const [a, b = a] = local.split(":");
  const part = (p: string): { row: number; col: number } | null => {
    const m = /^([A-Za-z]{0,3})(\d*)$/.exec(p.trim());
    if (!m || (!m[1] && !m[2])) return null;
    let col = 0;
    for (const ch of m[1].toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
    return { row: m[2] ? Number(m[2]) - 1 : -1, col: m[1] ? col - 1 : -1 };
  };
  const p = part(a);
  const q = part(b);
  if (!p || !q || (p.row < 0) !== (q.row < 0) || (p.col < 0) !== (q.col < 0)) return null;
  return {
    sheet,
    row: p.row < 0 ? 0 : Math.min(p.row, q.row),
    rows: p.row < 0 ? MAX_ROWS : Math.abs(q.row - p.row) + 1,
    col: p.col < 0 ? 0 : Math.min(p.col, q.col),
    cols: p.col < 0 ? MAX_COLS : Math.abs(q.col - p.col) + 1,
  };
}

/** Every area of a (possibly multi-area, comma-separated) address; unparsable areas are left out. */
export function rectsOfAddress(sheet: string, address: string): SheetRect[] {
  return address
    .split(",")
    .map((a) => rectOfAddress(sheet, a.trim()))
    .filter((r): r is SheetRect => r !== null);
}

const isFormula = (v: unknown): v is string => typeof v === "string" && v.startsWith("=");
const formulaOf = (r: Excel.Range): string => {
  const f = r.formulas?.[0]?.[0];
  return isFormula(f) ? f : "";
};

/** Cells Excel refused to recalculate: warned about once. */
const refused = new Set<string>();

function noteRefused(c: FormulaCell, e: unknown): void {
  const id = `${c.sheet}!${c.row},${c.col}`;
  if (refused.has(id)) return;
  refused.add(id);
  console.warn(`EAV: recalculating ${c.sheet} R${c.row + 1}C${c.col + 1} failed`, e);
}

const noteAccepted = (c: FormulaCell) => refused.delete(`${c.sheet}!${c.row},${c.col}`);

function reenterNow(cells: FormulaCell[]): Promise<Reentry[]> {
  return Excel.run(async (context) => {
    const names = Array.from(new Set(cells.map((c) => c.sheet)));
    const sheets = new Map(names.map((n) => [n, context.workbook.worksheets.getItemOrNullObject(n)] as [string, Excel.Worksheet]));
    sheets.forEach((ws) => ws.load("isNullObject"));
    await context.sync();
    const out: Reentry[] = cells.map((c) =>
      sheets.get(c.sheet)!.isNullObject ? { formula: "", reentered: false, sheetGone: true } : { formula: "", reentered: false }
    );
    const live = cells.map((_, i) => i).filter((i) => !out[i].sheetGone);
    // Read and recalculate in ONE batch.
    const calc = (i: number) => {
      const r = sheets.get(cells[i].sheet)!.getRangeByIndexes(cells[i].row, cells[i].col, 1, 1);
      r.load("formulas");
      r.calculate();
      return r;
    };
    const settle = (i: number, r: Excel.Range) => {
      out[i].formula = formulaOf(r);
      out[i].reentered = out[i].formula !== "";
      noteAccepted(cells[i]);
    };
    const ranges = new Map(live.map((i) => [i, calc(i)] as [number, Excel.Range]));
    try {
      await context.sync();
      ranges.forEach((r, i) => settle(i, r));
    } catch {
      // A cell failed and the batch stopped there: one cell at a time.
      for (const i of live) {
        try {
          const r = calc(i);
          await context.sync();
          settle(i, r);
        } catch (e) {
          noteRefused(cells[i], e);
          out[i].formula = null;
        }
      }
    }
    return out;
  });
}

/** The part of `a` inside `b` (same sheet), or null when they do not meet. */
function clip(a: SheetRect, b: SheetRect): SheetRect | null {
  const row = Math.max(a.row, b.row);
  const col = Math.max(a.col, b.col);
  const rows = Math.min(a.row + a.rows, b.row + b.rows) - row;
  const cols = Math.min(a.col + a.cols, b.col + b.cols) - col;
  return rows > 0 && cols > 0 ? { sheet: b.sheet, row, col, rows, cols } : null;
}

/**
 * Read the formulas of `scope`: each sheet's used range (values only, so
 * formatting cannot widen it), a selection clipped to it (a whole column
 * selected reads only the used rows), then one `formulas` load per rectangle.
 */
function formulasNow(scope: FormulaScope): Promise<ScopedFormula[]> {
  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    const active = worksheets.getActiveWorksheet();
    active.load("name");
    worksheets.load("items/name");
    const selected = scope === "selection" ? context.workbook.getSelectedRanges() : null;
    selected?.load("address");
    await context.sync();
    const names = scope === "workbook" ? worksheets.items.map((ws) => ws.name) : [active.name];
    const used = names.map((name) => {
      const r = worksheets.getItem(name).getUsedRangeOrNullObject(true);
      r.load("address");
      return { name, r };
    });
    await context.sync();
    const rects: SheetRect[] = [];
    for (const { name, r } of used) {
      const area = r.isNullObject ? null : rectOfAddress(name, r.address);
      if (!area) continue;
      if (!selected) rects.push(area);
      else rectsOfAddress(name, selected.address).forEach((s) => {
        const part = clip(s, area);
        if (part) rects.push(part);
      });
    }
    const ranges = rects.map((rect) => {
      const range = worksheets.getItem(rect.sheet).getRangeByIndexes(rect.row, rect.col, rect.rows, rect.cols);
      range.load("formulas");
      return { rect, range };
    });
    if (!ranges.length) return [];
    await context.sync();
    const out: ScopedFormula[] = [];
    ranges.forEach(({ rect, range }) =>
      range.formulas.forEach((line, i) =>
        line.forEach((f, j) => {
          if (isFormula(f)) out.push({ sheet: rect.sheet, row: rect.row + i, col: rect.col + j, formula: f });
        })
      )
    );
    return out;
  });
}

/** The Excel-backed FormulaPort (one set of event handlers per port). */
export function createFormulaPort(): FormulaPort {
  let watching = false;
  return {
    reenter: (cells) => (cells.length ? reenterNow(cells) : Promise.resolve([])),

    formulasIn: formulasNow,

    sheetNames: () =>
      Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/name");
        await context.sync();
        return sheets.items.map((ws) => ws.name);
      }),

    watch(events) {
      if (watching) return;
      watching = true;
      Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.onChanged.add(async (e: Excel.WorksheetChangedEventArgs) => {
          // ExcelApi 1.14 names the add-in's own changes; older hosts leave
          // triggerSource out, and isOwnWrite tells them apart instead.
          if (e.triggerSource === "ThisLocalAddin") return;
          try {
            const name = await Excel.run(async (ctx) => {
              const ws = ctx.workbook.worksheets.getItem(e.worksheetId);
              ws.load("name");
              await ctx.sync();
              return ws.name;
            });
            for (const rect of rectsOfAddress(name, e.address)) {
              if (e.triggerSource !== undefined || !isOwnWrite(rect)) events.change(rect);
            }
          } catch (err) {
            console.error("EAV: reading a worksheet change failed", err);
          }
        });
        sheets.onDeleted.add(async () => events.sheetDeleted());
        await context.sync();
      }).catch((err) => {
        watching = false;
        console.error("EAV: watching worksheet changes failed", err);
      });
    },
  };
}

export const excelFormulaPort: FormulaPort = createFormulaPort();
